import { buildResourcefulRequests, executeRequest } from "@utils/requests"
import Http from "@shared/http"
import Routes from "@utils/routes"

const wealthManagerRequests = () => buildResourcefulRequests(Routes.apiFspAdminWealthManagersPath())
const paraplannerRequests = () => buildResourcefulRequests(Routes.apiFspAdminParaplannersPath())
const assignmentRequests = () => buildResourcefulRequests(Routes.apiFspAdminParaplannerAssignmentsPath())

const searchQuery = (q?: string) => (q ? `q=${encodeURIComponent(q)}` : "")

// Array values go out as `k[]=` so Rails parses them back into an array.
const clientQuery = (params: ClientListParams) => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === "") return
    if (Array.isArray(v)) {
      v.forEach((item) => query.append(`${k}[]`, String(item)))
    } else {
      query.set(k, String(v))
    }
  })
  return query.toString()
}

export const api = {
  wealthManagers: {
    index: async (q?: string) => wealthManagerRequests().index(searchQuery(q)),
    create: async (attrs: WealthManagerAttrs) => wealthManagerRequests().create({ wealth_manager: attrs }),
    invite: async (id: number) => executeRequest(`${Routes.apiFspAdminWealthManagersPath()}/${id}/invite`, "PUT"),
    deactivate: async (id: number) => executeRequest(`${Routes.apiFspAdminWealthManagersPath()}/${id}/deactivate`, "PUT"),
    reactivate: async (id: number) => executeRequest(`${Routes.apiFspAdminWealthManagersPath()}/${id}/reactivate`, "PUT"),
  },
  paraplanners: {
    index: async (q?: string) => paraplannerRequests().index(searchQuery(q)),
    create: async (attrs: ParaplannerAttrs) => paraplannerRequests().create({ paraplanner: attrs }),
    invite: async (id: number) => executeRequest(`${Routes.apiFspAdminParaplannersPath()}/${id}/invite`, "PUT"),
    deactivate: async (id: number) => executeRequest(`${Routes.apiFspAdminParaplannersPath()}/${id}/deactivate`, "PUT"),
    reactivate: async (id: number) => executeRequest(`${Routes.apiFspAdminParaplannersPath()}/${id}/reactivate`, "PUT"),
  },
  assignments: {
    index: async () => assignmentRequests().index(),
    create: async (attrs: AssignmentAttrs) => assignmentRequests().create({ paraplanner_assignment: attrs }),
    update: async (id: number, attrs: AssignmentAttrs) => assignmentRequests().update({ id, ...{ paraplanner_assignment: attrs } } as any),
    destroy: async (id: number) => assignmentRequests().delete(id),
  },
  clients: {
    index: async (params: ClientListParams) =>
      executeRequest(`${Routes.apiFspAdminClientsPath()}?${clientQuery(params)}`),
    // Every id matching the filters, across all pages — backs select-all.
    ids: async (filters: ClientListFilters): Promise<{ ids: number[] }> =>
      executeRequest(`${Routes.apiFspAdminClientsIdsPath()}?${clientQuery(filters)}`),
    export: async (filters: ClientListFilters) => {
      const response = await Http.request(
        Routes.apiFspAdminClientsExportPath(),
        "POST",
        JSON.stringify(filters)
      )
      if (!response.ok) throw new Error("Export request failed")
      return response.json()
    },
  },
  clientTransfers: {
    bulkCreate: async (payload: BulkTransferPayload) => {
      const response = await Http.request(
        Routes.apiFspAdminClientTransfersBulkCreatePath(),
        "POST",
        JSON.stringify(payload)
      )
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || "Bulk transfer failed")
      return body
    },
  },
}

export type WealthManagerAttrs = { first_name: string; last_name: string; email: string; cellphone_number: string }
export type ParaplannerAttrs = WealthManagerAttrs
export type AssignmentAttrs = {
  wealth_manager_id?: number
  paraplanner_id?: number
  access_strategy: "allow_all" | "allow_some" | "deny_some" | "allow_none"
  client_ids?: number[]
}
export type ClientListParams = {
  page?: number
  per_page?: number
  q?: string
  wealth_manager_id?: number
  status?: string
  active?: boolean
  ids?: number[]
  // "<column> <asc|desc>" — column must be in FspAdmin::ClientListQuery::SORTABLE_COLUMNS.
  sort?: string
}
export type ClientListFilters = Omit<ClientListParams, "page" | "per_page">
export type BulkTransferPayload = {
  from_wealth_manager_id: number
  to_wealth_manager_id: number
  client_ids: number[]
}
